/**
 * The Bloodline bank: where Legacy lives between runs.
 *
 * Kept in the UI layer for the same reason `storage.ts` is: the simulation is
 * headless and may not touch `localStorage`, and a bank that outlives every
 * run has no business inside a run save — a player who deletes a dead
 * campaign's slot is not disinheriting the family. So this is **its own key**
 * (`hearth.bloodline.v1`), never inside `SAVE_KEY`, never in `SaveGame`, and
 * the simulation meets it exactly once: the menu reads `owned()` into
 * `WorldConfig.bloodline` when a run begins, and the death screen pays
 * `bankRun` what `legacyEarned` says the run was worth.
 *
 * The store is a class with the key–value pair injected, on `savestore.ts`'s
 * pattern, so the tests can run it in Node against a plain object; the module
 * singleton binds `localStorage` lazily and forgets gracefully where there is
 * none — an environment without storage gets a bank that lasts the session,
 * which is better than one that throws on every death.
 *
 * Everything here is refusal-checked the way `postRefusal` is: `buy` returns
 * the sentence saying why not, and the screen prints that sentence rather
 * than inventing its own — one rule, one string.
 */

import { BLOODLINE_UNLOCKS, bloodlineUnlock, type BloodlineUnlockDef } from '../data/bloodline.ts';

export interface BloodlineBank {
  v: 1;
  /** Spendable Legacy. */
  legacy: number;
  /** Everything ever banked, spent or not — the family's whole history. */
  earnedTotal: number;
  /** Unlock ids bought, in purchase order. Order is display-only: the fold order is the catalogue's. */
  unlocked: string[];
  /** Runs that have ended and been banked. */
  runs: number;
  /** The deepest wave any banked run reached. */
  bestWave: number;
}

function emptyBank(): BloodlineBank {
  return { v: 1, legacy: 0, earnedTotal: 0, unlocked: [], runs: 0, bestWave: 0 };
}

interface KV {
  getItem(k: string): string | null;
  setItem(k: string, v: string): void;
}

export const BLOODLINE_KEY = 'hearth.bloodline.v1';

export class BloodlineStore {
  private bank: BloodlineBank;

  constructor(private kv: KV, private key: string = BLOODLINE_KEY) {
    this.bank = this.read();
  }

  /**
   * Tolerant on purpose: a garbled bank loads as an empty one rather than a
   * crash on the title screen, and every field is `??`-defaulted so a bank
   * written by an older shape keeps what it had. Unlock ids are filtered
   * against the catalogue at *read* time — an id from a newer build stays in
   * the stored json (writing would destroy it) but is never listed or folded.
   */
  private read(): BloodlineBank {
    let raw: unknown;
    try {
      const json = this.kv.getItem(this.key);
      if (!json) return emptyBank();
      raw = JSON.parse(json);
    } catch {
      return emptyBank();
    }
    if (typeof raw !== 'object' || raw === null) return emptyBank();
    const b = raw as Partial<BloodlineBank>;
    const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : 0);
    return {
      v: 1,
      legacy: num(b.legacy),
      earnedTotal: num(b.earnedTotal),
      unlocked: Array.isArray(b.unlocked) ? b.unlocked.filter((id): id is string => typeof id === 'string') : [],
      runs: num(b.runs),
      bestWave: num(b.bestWave),
    };
  }

  private write(): void {
    try {
      this.kv.setItem(this.key, JSON.stringify(this.bank));
    } catch {
      // Quota, or storage switched off. The in-memory bank still works for
      // the session, which is the most that can honestly be offered.
    }
  }

  /** A copy, for screens. Mutating it changes nothing. */
  state(): BloodlineBank {
    return { ...this.bank, unlocked: this.owned() };
  }

  /** The ids a new run should carry — bought *and* known to this build. */
  owned(): string[] {
    return this.bank.unlocked.filter((id) => bloodlineUnlock(id) !== undefined);
  }

  /**
   * Credit a finished run. Finiteness first, comparisons after — the
   * `postRefusal` lesson; a NaN paid into a bank that persists would outlive
   * every run that ever noticed it.
   */
  bankRun(earned: number, wave: number): void {
    if (!Number.isFinite(earned) || earned < 0) return;
    const amount = Math.floor(earned);
    this.bank.legacy += amount;
    this.bank.earnedTotal += amount;
    this.bank.runs += 1;
    if (Number.isFinite(wave) && wave > this.bank.bestWave) this.bank.bestWave = Math.floor(wave);
    this.write();
  }

  /**
   * Buy an unlock, or say in one sentence why not. The screen prints the
   * sentence verbatim.
   */
  buy(id: string): { ok: boolean; why?: string } {
    const def = bloodlineUnlock(id);
    if (!def) return { ok: false, why: 'No such unlock is written in the book.' };
    if (this.bank.unlocked.includes(id)) return { ok: false, why: 'The family already holds this.' };
    if (def.requires && !this.bank.unlocked.includes(def.requires)) {
      const below = bloodlineUnlock(def.requires);
      return { ok: false, why: `${below?.name ?? 'The step below'} must be held first.` };
    }
    if (this.bank.legacy < def.cost) {
      return { ok: false, why: `It costs ${def.cost} Legacy, and the family holds ${this.bank.legacy}.` };
    }
    this.bank.legacy -= def.cost;
    this.bank.unlocked.push(id);
    this.write();
    return { ok: true };
  }
}

// ── The singleton the game uses ──────────────────────────────────────────────

let store: BloodlineStore | null = null;

/**
 * Built lazily for the same reason `storage.ts`'s browser store is: this
 * module is imported by tests running in Node, where touching `localStorage`
 * at import time would throw.
 */
export function bloodline(): BloodlineStore {
  if (!store) {
    const forgetful: KV = (() => {
      let held: string | null = null;
      return { getItem: () => held, setItem: (_k, v) => void (held = v) };
    })();
    const kv: KV = typeof localStorage !== 'undefined' ? localStorage : forgetful;
    store = new BloodlineStore(kv);
  }
  return store;
}

/** The unlock ids a new run starts with. Sugar for the two call sites in `main.ts`. */
export function ownedBloodline(): string[] {
  return bloodline().owned();
}

// ── Read-model for the menu screen ───────────────────────────────────────────

export type UnlockState = 'owned' | 'ready' | 'poor' | 'locked';

export interface UnlockRow {
  def: BloodlineUnlockDef;
  state: UnlockState;
}

/**
 * Each catalogue entry with the one word the screen needs. 'ready' means
 * buyable now; 'poor' means only Legacy is missing — the screen shows those
 * as the goal to save for; 'locked' means the step below is not held.
 */
export function unlockRows(bank: BloodlineBank): UnlockRow[] {
  const held = new Set(bank.unlocked);
  return BLOODLINE_UNLOCKS.map((def) => {
    let state: UnlockState;
    if (held.has(def.id)) state = 'owned';
    else if (def.requires && !held.has(def.requires)) state = 'locked';
    else state = def.cost <= bank.legacy ? 'ready' : 'poor';
    return { def, state };
  });
}
